import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDevice } from '../hooks/useDevice';
import ResponsiveContainer from './layout/ResponsiveContainer';
// CB:BEGIN content-limiter-import
import ContentLimiter from './layout/ContentLimiter';
// CB:END content-limiter-import
import { SignInButton, SignedIn, SignedOut } from '@clerk/clerk-react';
import { AddressParser } from '../utils/addressParser';
import { useProgressiveDataService, getTempSessionId } from '../lib/progressiveDataService';
import { TempUserService } from '../lib/tempUserService';
import { trackAnalyticsEvent } from '../lib/analytics';
import { getApiBaseUrl } from '../lib/getApiUrl';
// import { useMobile } from '../contexts/MobileContext';
// import { showDevNotification } from '../lib/devToast';
import { FaTwitter, FaLinkedin, FaInstagram, FaFacebook } from 'react-icons/fa';
import { eventTracker } from './DataTracking';
import { extractZipFromAddress } from '../lib/zip';

// CSS animations for shine effects
const shineStyles = `
  @keyframes shine {
    0% { left: -100%; }
    100% { left: 100%; }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
`;

interface LandingPageProps {
  onNavigate: (screen: string) => void;
}

// Debug logs removed for production

// Features removed - not currently used

const testimonials = [
  {
    quote:
      'CoreBids made my roof replacement so easy! The estimate was fast and the contractor was professional. Highly recommend!',
    author: '— John D., Nashville',
    rating: 5
  },
  {
    quote:
      'I got three bids for my roofing project and saved over $2,000. The process was simple and the team was helpful every step of the way.',
    author: '— Maria S., Brentwood',
    rating: 5
  },
  {
    quote:
      "The online dashboard made it easy to track my project and communicate with my contractor. I'll use CoreBids again!",
    author: '— Steve R., Franklin',
    rating: 5
  },
  {
    quote:
      'Amazing service! Got my roof estimate in under 24 hours and saved $1,500 compared to other quotes. The contractors were all top-notch.',
    author: '— Sarah M., Hendersonville',
    rating: 5
  },
  {
    quote:
      'As a first-time homeowner, I was nervous about getting roof work done. CoreBids made the whole process transparent and stress-free.',
    author: '— Michael T., Murfreesboro',
    rating: 5
  },
  {
    quote:
      'Professional, reliable, and saved me money. What more could you ask for? My new roof looks fantastic and the installation was perfect.',
    author: '— Lisa K., Smyrna',
    rating: 5
  },
  {
    quote:
      'Incredibly simple! I received multiple quotes in just 24 hours without ever leaving my house or talking to anyone. The whole process was completely hassle-free.',
    author: '— David L., Nashville',
    rating: 5
  }
];

const LandingPage: React.FC<LandingPageProps> = ({ onNavigate }) => {
  // TDZ-safe: resolve API base inside component
  const apiBaseUrl = React.useMemo(() => getApiBaseUrl(), []);
  // CB:BEGIN fix-invalid-hook – call hook at top level; use in callbacks (do not call useProgressiveDataService inside handlers)
  const { createOrUpdateTempHomeowner } = useProgressiveDataService();
  // CB:END fix-invalid-hook

  const [address, setAddress] = useState<string>('');
  const [parsedAddress, setParsedAddress] = useState<any>(null);
  // CB:BEGIN state-variables
  // State variables for estimate generation and UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showQuickEstimate, setShowQuickEstimate] = useState<boolean>(false);
  const [isZipLookupLoading, setIsZipLookupLoading] = useState<boolean>(false);
  const [isGeneratingEstimate, setIsGeneratingEstimate] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [estimateData, setEstimateData] = useState<any>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [estimateId, setEstimateId] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Handle video playback when state changes
  useEffect(() => {
    if (isVideoPlaying && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Video playback failed:', error);
        // If autoplay fails, keep the video visible so user can manually play
      });
    }
  }, [isVideoPlaying]);
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isEstimateRefined, setIsEstimateRefined] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentWordIndex, setCurrentWordIndex] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isFloatingOut, setIsFloatingOut] = useState<boolean>(false);
  // CB:END state-variables
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState<number>(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Autocomplete
  const [suggestions, setSuggestions] = useState<unknown[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Refs
  const addressInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const ctaButtonRef = React.useRef<HTMLButtonElement>(null);
  const ignorePredictionsRef = useRef<boolean>(false);

  const { ensureActiveProject } = useProgressiveDataService();



  useEffect(() => {
    // Track landing page view
    eventTracker.track('landing_page_view');
    
    // CB:BEGIN force-demo-mode-landing
    // Ensure demo mode is enabled on landing so automated emails/bids run
    import('../lib/featureFlags')
      .then(({ enableDemoMode }) => {
        if (typeof enableDemoMode === 'function') enableDemoMode();
      })
      .catch(() => {});
    // CB:END force-demo-mode-landing
    
    // Auto-rotate testimonials every 4 seconds
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => prev + 1);
    }, 4000);
    
    return () => clearInterval(testimonialInterval);
  }, []);

  useEffect(() => {
    // Initialize temp session ID if not exists
    if (!getTempSessionId()) {
      const newSessionId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('temp_session_id', newSessionId);
    }
    // showDevNotification('🧪 Dev mode active - notifications enabled');
  }, []);

  // Floating word rotation
  useEffect(() => {
    const interval = setInterval(() => {
      // Start floating out
      setIsFloatingOut(true);
      
      // After float out completes, change word and float in
          setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % 3);
        setIsFloatingOut(false);
      }, 1000); // Wait for float out animation to complete
    }, 4000); // Total cycle: 4 seconds

    return () => clearInterval(interval);
  }, []);



  // --- Autocomplete functions ---
  const fetchSuggestions = async (input: string) => {
    if (input.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const url = `${apiBaseUrl}/api/autocomplete?input=${encodeURIComponent(input)}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        if (data.predictions) {
          setSuggestions(data.predictions);
          setShowSuggestions(true);
          setSelectedSuggestionIndex(-1);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } else {
        console.error('Backend autocomplete error:', res.status);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);

    setEstimateData(null);
    setEstimateId('');
    setIsEstimateRefined(false);

    if (ignorePredictionsRef.current) {
      ignorePredictionsRef.current = false;
      return;
    }

    if (value.length >= 3) {
      fetchSuggestions(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (suggestion: any) => {
    const label = suggestion.description || '';
    setAddress(label);
    setShowSuggestions(false);
    setSuggestions([]);
    ignorePredictionsRef.current = true;

    // Track address selection
    eventTracker.track('address_entered');

    // CB:BEGIN save-address-on-selection-desktop
    // Save address to localStorage and temp homeowner immediately when selected
    try {
      localStorage.setItem('enteredAddress', label);
      // Clear stale address keys to prevent overwriting
      localStorage.removeItem('pendingUserAddress');
      localStorage.removeItem('last_address');
    } catch {}

    // Save to temp homeowner immediately (createOrUpdateTempHomeowner from hook at top level)
    try {
      const tempSessionId = getTempSessionId();
      if (tempSessionId && label) {
        await createOrUpdateTempHomeowner({
          temp_session_id: tempSessionId,
          address: label,
        });
        console.log('✅ Address saved to temp homeowner:', label);
      }
    } catch (error) {
      console.warn('⚠️ Failed to save address to temp homeowner:', error);
    }
    // CB:END save-address-on-selection-desktop

    if (addressInputRef.current) {
      addressInputRef.current.focus();
      addressInputRef.current.setSelectionRange(
        label.length,
        label.length
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
      e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
      e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
      e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSuggestionClick(suggestions[selectedSuggestionIndex]);
        } else {
          handleGenerateEstimate(e);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
      setSuggestions([]);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      setSuggestions([]);
    }
  };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function highlight(mainText: string, searchTerm?: string) {
    if (!searchTerm || !mainText) return mainText;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return mainText.replace(regex, '<mark>$1</mark>');
  }

  const handleGenerateEstimate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;

    // Prevent multiple submissions
    if (isZipLookupLoading || isGeneratingEstimate) {
      return;
    }

    // Track address form submission
    eventTracker.track('address_form_entered');

    // CB:BEGIN save-address-on-submit-desktop
    // Save address to localStorage and temp homeowner when submitting
    try {
      localStorage.setItem('enteredAddress', address.trim());
    } catch {}

    // Save to temp homeowner immediately (createOrUpdateTempHomeowner from hook at top level)
    try {
      const tempSessionId = getTempSessionId();
      if (tempSessionId && address.trim()) {
        await createOrUpdateTempHomeowner({
          temp_session_id: tempSessionId,
          address: address.trim(),
        });
        console.log('✅ Address saved to temp homeowner on submit:', address.trim());
      }
    } catch (error) {
      console.warn('⚠️ Failed to save address to temp homeowner on submit:', error);
    }
    // CB:END save-address-on-submit-desktop

    try {
      console.log('🔍 Starting address parsing for:', address);
      setIsZipLookupLoading(true);
      
      const parsed = await AddressParser.parseAddressWithZipLookup(address);
      setParsedAddress(parsed);
      
      console.log('📍 Parsed address with ZIP lookup:', parsed);
      console.log('🔍 ZIP code from parser:', parsed?.zipCode);

      // Skip quick estimate generation — go straight to the full estimate form
      setIsZipLookupLoading(false);
      handleGetEstimates();
      } catch (error) {
        console.error('Error parsing address:', error);
        setIsZipLookupLoading(false);
        setShowQuickEstimate(true);
      }
  };

  // CB:BEGIN unused-quick-estimate
  // Function kept for potential future use
   
  const _generateQuickEstimate = async (googleData: any, parsedAddress: any) => {
    try {
      console.log('🚀 Generating quick roof estimate...');

      const { place_id } = googleData;
      const { zipCode } = parsedAddress;

      // Fallback: try to extract zip from address if parsedAddress.zipCode is missing
      let zipToUse = zipCode;
      if (!zipToUse && address) {
        const zipMatch = address.match(/\b\d{5}\b/);
        if (zipMatch) {
          zipToUse = zipMatch[0];
          console.log('🔍 Extracted ZIP from address:', zipToUse);
        }
      }
      
      if (!zipToUse) {
        throw new Error('ZIP code is required but could not be determined');
      }

      let lat: number | undefined;
      let lon: number | undefined;

      if (googleData.geometry?.location?.lat && googleData.geometry?.location?.lng) {
        lat = googleData.geometry.location.lat;
        lon = googleData.geometry.location.lng;
      } else if (googleData.coordinates?.lat && googleData.coordinates?.lng) {
        lat = googleData.coordinates.lat;
        lon = googleData.coordinates.lng;
      } else {
        throw new Error('Could not get coordinates from address');
      }

      // CB:BEGIN address-fix
      // Use the original user input address instead of Google's formatted version
      const addressToUse = address;
      // CB:END address-fix

      const projectId = `project_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const requestBody = {
        projectId,
        formattedAddress: addressToUse,
        zip: zipToUse,
        placeId: place_id || 'fallback',
        lat,
        lon
      };
      
      console.log('📤 Sending request to API:', requestBody);
      console.log('🔍 zipCode value:', zipCode);
      console.log('🔍 lat/lon values:', { lat, lon });
      
      const response = await fetch(`${apiBaseUrl}/api/roof/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const estimateResult = await response.json();
      console.log('✅ Quick estimate generated:', estimateResult);

      console.log('✅ Setting estimate data:', estimateResult);
      setEstimateData(estimateResult);
      setEstimateId(projectId);
      setIsEstimateRefined(true);
      setShowQuickEstimate(true);
      
      // Save estimate data and address to localStorage for ProjectWizard
      try {
        localStorage.setItem('quickEstimate', JSON.stringify(estimateResult));
        localStorage.setItem('enteredAddress', addressToUse);
        console.log('💾 Saved estimate data to localStorage');
      } catch (error) {
        console.error('❌ Error saving to localStorage:', error);
      }
      
      // Redirect to ProjectWizard after a short delay
      setTimeout(() => {
        onNavigate('project-wizard');
      }, 1500);
      
      // showDevNotification('✅ Roof estimate generated!');
    } catch (error) {
      console.error('❌ Error generating quick estimate:', error);
      setShowQuickEstimate(true);
    }
  };

  const resolveStoredAddress = () => {
    try {
      return (
        localStorage.getItem('pendingUserAddress') ||
        localStorage.getItem('enteredAddress') ||
        localStorage.getItem('last_address') ||
        ''
      );
    } catch {
      return '';
    }
  };

  const handleGetEstimates = useCallback(async () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }

    try {
      const address = resolveStoredAddress();
      const quickEstimate = localStorage.getItem('quickEstimate') || undefined;

      const ensured = await TempUserService.ensureTempHomeowner({
        address,
        quick_estimate: quickEstimate,
      });

      await ensureActiveProject({
        tempHomeownerId: ensured.tempHomeownerId,
        tempSessionId: ensured.tempSessionId,
        address,
        projectType: localStorage.getItem('pendingProjectType'),
        logLabel: '[Landing CTA]',
      });

        trackAnalyticsEvent('wizard_started', {
          path: '/start',
          projectId: localStorage.getItem('active_project_id') || undefined,
        });
    } catch (error) {
      console.error('❌ Failed to ensure temp project before navigation', error);
    } finally {
      onNavigate('estimate-form');
    }
  }, [ensureActiveProject, onNavigate]);

  // CB:BEGIN unused-continue-handler
  // Function kept for potential future use
   
  const _handleContinueToQuestions = (): void => {
    handleGetEstimates();
  };
  // CB:END unused-continue-handler

  const { isMobileLayout } = useDevice();

  // Header helpers (for Figma header)
  const scrollToHero = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      if (addressInputRef.current) {
        addressInputRef.current.focus();
      }
    }, 500);
  };

  // CB:BEGIN unused-scroll-section
  // Function kept for potential future use
   
  const _scrollToSection = (_id: string): void => {
    const el = document.getElementById(_id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  // CB:END unused-scroll-section

  // Load Enhancify payment calculator script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.enhancify.com/paymentcalculatorwidget/';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const existingScript = document.querySelector('script[src="https://www.enhancify.com/paymentcalculatorwidget/"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <ResponsiveContainer className="container light-mode-override" >
      {/* CSS Animations */}
      <style>{shineStyles}</style>
      
      {/* Header */}
      <header style={{
          position: 'sticky',
          top: 0,
          background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        padding: isMobileLayout ? '14px 16px' : 'clamp(1rem, 2vw, 1.5rem) 0',
          zIndex: 100,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
      }}>
        <ContentLimiter>
          <div style={{
              display: 'flex',
              justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {/* Logo */}
            <div style={{
                display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer'
            }}
            onClick={scrollToHero}>
              <img 
                src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/1e6de21b4640ee3caf75e72754aa18f5a9d49e49/CoreBids%20Logo%20(Combo).png"
                alt="CoreBids Logo"
              style={{
                  height: isMobileLayout ? 40 : 'clamp(40px, 6vw, 50px)',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
              </div>

            {/* Nav - Desktop */}
            {!isMobileLayout && (
              <nav style={{
                display: 'flex',
                gap: 'clamp(1.5rem, 3vw, 2.5rem)',
                alignItems: 'center'
              }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate('contractor-landing');
                  }}
                  style={{
                    color: '#0f172a',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#123c3b'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#0f172a'}
                >
                  For Contractors
                </a>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate('faq');
                  }}
                style={{
                    color: '#0f172a',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#123c3b'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#0f172a'}
                >
                  FAQ
                </a>
                <a
                  href="https://www.enhancify.com/corebids"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#0f172a',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                    transition: 'color 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#123c3b'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#0f172a'}
                >
                  Financing
                </a>
                <SignedOut>
                  <SignInButton mode="redirect">
                <button
                  style={{
                        background: '#123c3b',
                    color: '#ffffff',
                        border: 'none',
                        padding: 'clamp(0.625rem, 1.5vw, 0.875rem) clamp(1.25rem, 2.5vw, 1.75rem)',
                        borderRadius: '0.5rem',
                        fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                    fontWeight: 700,
                    cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 8px rgba(18, 60, 59, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(18, 60, 59, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(18, 60, 59, 0.2)';
                      }}
                    >
                      Login
                </button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
              <button
                onClick={scrollToHero}
                style={{
                  background: '#123c3b',
                  color: '#ffffff',
                      border: 'none',
                      padding: 'clamp(0.625rem, 1.5vw, 0.875rem) clamp(1.25rem, 2.5vw, 1.75rem)',
                      borderRadius: '0.5rem',
                      fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                      fontWeight: 700,
                  cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(18, 60, 59, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(18, 60, 59, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(18, 60, 59, 0.2)';
                    }}
                  >
                    Get Started
            </button>
                </SignedIn>
              </nav>
              )}

            {/* Mobile Menu Button */}
            {isMobileLayout && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#123c3b',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                {isMobileMenuOpen ? '✕' : '☰'}
              </button>
            )}
          </div>
        </ContentLimiter>
      </header>

      {/* Mobile Menu Dropdown */}
      {isMobileLayout && isMobileMenuOpen && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: 0,
          right: 0,
          bottom: 0,
          background: '#ffffff',
          zIndex: 1000,
          padding: '2rem',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <a
              href="#"
                  onClick={(e) => {
                    e.preventDefault();
                onNavigate('contractor-landing');
                setIsMobileMenuOpen(false);
              }}
              style={{
                color: '#0f172a',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1.125rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid #e2e8f0'
              }}
            >
              For Contractors
                </a>
                <a
                  href="https://www.enhancify.com/corebids"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{
                    color: '#0f172a',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #e2e8f0'
                  }}
                >
                  Financing
                </a>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                onNavigate('faq');
                setIsMobileMenuOpen(false);
              }}
              style={{
                color: '#0f172a',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1.125rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid #e2e8f0'
              }}
            >
              FAQ
            </a>
            <SignedOut>
              <SignInButton mode="redirect">
                <button
                  style={{
                    background: '#123c3b',
                    color: '#ffffff',
                    border: 'none',
                    padding: '0.875rem 1.75rem',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    width: '100%',
                    marginTop: '1rem'
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Login
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <button
                onClick={() => {
                  scrollToHero();
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  background: '#123c3b',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.875rem 1.75rem',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: '1rem'
                }}
              >
                Get Started
              </button>
            </SignedIn>
            </div>
          </div>
      )}
      
      {/* Hero */}
      <section
        id="hero-section"
        style={{
          background: 'linear-gradient(135deg, #0d2928 0%, #e8f5f4 65%, #ffffff 100%)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 'clamp(600px, 80vh, 900px)',
          display: 'flex',
          alignItems: 'center',
          padding: 'clamp(2rem, 6vw, 4rem) 0'
        }}
      >
        {/* Background decoration */}
        <div style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          pointerEvents: 'none'
        }}>
          <div style={{
            position: 'absolute',
            top: '-10%',
            left: '-10%',
            width: '300px',
            height: '300px',
            background: '#ffffff',
            borderRadius: '50%',
            filter: 'blur(60px)'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-10%',
            right: '-10%',
            width: '300px',
            height: '300px',
            background: '#ffffff',
            borderRadius: '50%',
            filter: 'blur(60px)'
          }} />
        </div>

        {/* CB:BEGIN landing-hero-content-limit */}
        <ContentLimiter>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: 'clamp(2rem, 6vw, 4rem)',
            alignItems: 'center'
          }}>
            {/* Left Column - Text */}
            <div style={{ maxWidth: '600px' }}>
          <h1 style={{ 
                fontSize: 'clamp(2.5rem, 5vw + 1rem, 4rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                margin: 0,
                color: '#ffffff', 
                marginBottom: 'clamp(1.5rem, 4vw, 3rem)' 
          }}>
            Online Roof Estimates
            <span style={{ 
              display: 'block', 
              fontStyle: 'italic',
                  marginTop: 'clamp(0.5rem, 1vw, 1rem)'
                }}>
                  <span style={{ color: '#ffffff' }}>from </span>
                  <span style={{ 
                    color: '#FBBF24',
              position: 'relative',
                    display: 'inline-block'
            }}>
                    Real Pros
              <svg 
                style={{
                  position: 'absolute',
                        bottom: '-4px',
                        left: 0,
                        width: '100%',
                  height: '8px'
                }}
                viewBox="0 0 200 8"
                preserveAspectRatio="none"
              >
                <path 
                  d="M 0 8 Q 100 -4 200 8" 
                        stroke="#FBBF24"
                  strokeWidth="3" 
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
                  </span>
            </span>
          </h1>
          
              <div style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
                {[
                  { title: 'No Visits', desc: 'Get roof measured remotely.' },
                  { title: 'Contractors Compete', desc: 'Pros bid for your project.' },
                  { title: 'Save Money', desc: 'Compare & pick the best offer.' }
                ].map((item, i) => (
                  <div key={i} style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'clamp(0.75rem, 2vw, 1rem)',
                    marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)'
                  }}>
                    <span style={{ 
                      color: '#FBBF24', 
                      fontWeight: 700,
                      fontSize: 'clamp(0.5rem, 1vw, 0.625rem)',
                      marginTop: '0.25rem',
                      flexShrink: 0
                    }}>●</span>
                    <span style={{ 
                      fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
                      lineHeight: 1.6,
                      color: '#ffffff'
                    }}>
                      <strong>{item.title}</strong> — {item.desc}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* CTA Form */}
              <form onSubmit={handleGenerateEstimate} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 'clamp(0.75rem, 2vw, 1rem)',
                maxWidth: '600px'
              }}>
                <div className="relative" ref={suggestionsRef} style={{ position: 'relative' }}>
                <input
                  ref={addressInputRef}
                  type="text"
                  placeholder="Enter your address"
                  value={address}
                  onChange={handleAddressChange}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-form-type="other"
                  className="form-control no-autocomplete-styling"
                  style={{ 
                      padding: 'clamp(0.875rem, 2vw, 1.25rem) clamp(1rem, 3vw, 1.5rem)',
                      borderRadius: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                      border: '3px solid #ffffff',
                      fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                      fontWeight: 500,
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                      outline: 'none',
                      width: '100%',
                      background: '#ffffff',
                      color: '#0F172A'
                  }}
                  required
                  aria-autocomplete="list"
                  aria-controls="autocomplete-list"
                  aria-activedescendant={
                    selectedSuggestionIndex >= 0 ? `suggestion-${selectedSuggestionIndex}` : undefined
                  }
                  role="combobox"
                  aria-expanded={showSuggestions}
                />
                {showSuggestions && (
                  <ul
                    id="autocomplete-list"
                    className="absolute left-0 right-0 z-50 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-72 overflow-auto"
                    role="listbox"
                    style={{ 
                      minWidth: '100%', 
                      backgroundColor: 'white', 
                      fontSize: '15.75px', 
                      textAlign: 'left',
                      position: 'absolute',
                      zIndex: 9999,
                      top: '100%',
                      left: 0,
                      right: 0
                    }}
                  >
                    {suggestions.map((s: unknown, idx: number) => {
                      const fullAddress = (s as any).description;
                      const formattedAddress = fullAddress.replace(/, USA$/, '').replace(/\n/g, ', ');
                      return (
                        <React.Fragment key={(s as any).place_id}>
                          <li
                            id={`suggestion-${idx}`}
                            role="option"
                              aria-selected={selectedSuggestionIndex === idx}
                            className={`px-4 py-3 cursor-pointer transition-colors duration-100 ${
                                selectedSuggestionIndex === idx ? 'bg-blue-50' : 'hover:bg-gray-100'
                            }`}
                            style={{ fontSize: '15.75px', textAlign: 'left' }}
                            onClick={() => handleSuggestionClick(s)}
                              onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                          >
                            <div className="flex flex-col">
                                <span className="font-medium" style={{ fontSize: '15.75px', color: 'black' }}>
                                <span>{formattedAddress}</span>
                              </span>
                            </div>
                          </li>
                          {idx < suggestions.length - 1 && (
                              <div
                                style={{
                              height: '1px', 
                              backgroundColor: 'rgba(128, 128, 128, 0.5)', 
                              margin: '0 16px' 
                                }}
                              />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </ul>
                )}
              </div>
              <button
                ref={ctaButtonRef}
                type="submit"
                  className="cta-button continue-button-black"
                disabled={isZipLookupLoading || isGeneratingEstimate}
                style={{
                    background: (isZipLookupLoading || isGeneratingEstimate) ? '#f3f4f6' : '#ffffff',
                    color: (isZipLookupLoading || isGeneratingEstimate) ? '#9ca3af' : '#000000',
                  border: 'none',
                    padding: 'clamp(1rem, 2.5vw, 1.5rem) clamp(2rem, 5vw, 3rem)',
                    borderRadius: 'clamp(0.75rem, 1.5vw, 1rem)',
                    fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                  fontWeight: 700,
                  cursor: (isZipLookupLoading || isGeneratingEstimate) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 16px rgba(18, 60, 59, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)',
                    letterSpacing: '0.025em',
                    position: 'relative',
                    overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (isZipLookupLoading || isGeneratingEstimate) return;
                  const target = e.target as HTMLButtonElement;
                    target.style.transform = 'translateY(-3px)';
                    target.style.boxShadow = '0 8px 24px rgba(18, 60, 59, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)';
                    target.style.background = '#E8F5F4';
                }}
                onMouseLeave={(e) => {
                  if (isZipLookupLoading || isGeneratingEstimate) return;
                  const target = e.target as HTMLButtonElement;
                    target.style.transform = 'translateY(0)';
                    target.style.boxShadow = '0 4px 16px rgba(18, 60, 59, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)';
                    target.style.background = '#ffffff';
                }}
              >
                <span style={{ color: '#000000' }}>
                  {isZipLookupLoading ? 'Checking Service Area...' : 
                   isGeneratingEstimate ? 'Working...' : 
                   'Continue!'}
                </span>
              </button>
            </form>
        </div>
            
            {/* Right Column - Video/Image */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ 
              position: 'relative',
                width: '100%',
                maxWidth: '600px',
                aspectRatio: '16/9',
                borderRadius: 'clamp(0.75rem, 1.5vw, 1rem)',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
            }}>
              {!isVideoPlaying ? (
                <>
                  <img 
                    src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/004332ba3d8e389db5761f190f1551b66d55afe1/CoreBids%20Intro%20(1)-Cover.jpg"
                      alt="CoreBids Introduction"
                    style={{
                      width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {/* Play Button */}
                    <div 
                      onClick={() => setIsVideoPlaying(true)}
                      style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    zIndex: 10
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                  }}
                  >
                    <div style={{
                      width: 'clamp(60px, 8vw, 80px)',
                      height: 'clamp(60px, 8vw, 80px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
                    }}>
                      <div style={{
                        width: 0,
                        height: 0,
                          borderLeft: 'clamp(20px, 3vw, 25px) solid #ff0000',
                          borderTop: 'clamp(15px, 2.2vw, 18px) solid transparent',
                          borderBottom: 'clamp(15px, 2.2vw, 18px) solid transparent',
                          marginLeft: 'clamp(4px, 0.6vw, 6px)'
                      }} />
                    </div>
                  </div>
                </>
              ) : (
                <video
                  ref={videoRef}
                  controls
                  playsInline
                  preload="metadata"
                  style={{
                    width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onEnded={() => setIsVideoPlaying(false)}
                >
                  <source src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/004332ba3d8e389db5761f190f1551b66d55afe1/CoreBids%20Intro%20(1).mp4" type="video/mp4" />
                </video>
              )}
            </div>
          </div>
          </div>
        </div>
        </ContentLimiter>
        {/* CB:END landing-hero-content-limit */}
      </section>

      {/* Why CoreBids is Better */}
      <section style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        position: 'relative'
      }}>
        <ContentLimiter>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
            fontWeight: 700, 
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            margin: 0,
            color: '#0F172A',
            textAlign: 'center',
            marginBottom: 'clamp(1.5rem, 3vw, 2.5rem)'
          }}>
            Why CoreBids is Better
          </h2>

                    <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            gap: 'clamp(2rem, 4vw, 3rem)',
            marginTop: 'clamp(2rem, 4vw, 3rem)'
          }}>
            {[
              {
                title: 'Save Time',
                description: 'Get your project measured remotely—no home visits or calls needed.',
                image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/945c5c5bf84dd10ba5062e05aaaa4f62de774d3e/SaveTime2.png'
              },
              {
                title: 'Compare Easily',
                description: 'Local contractors compete, so you see multiple bids in minutes.',
                image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/945c5c5bf84dd10ba5062e05aaaa4f62de774d3e/ComparePrices.png'
              },
              {
                title: 'Peace of Mind',
                description: 'Accurate estimates from licensed, verified pros, no guesswork.',
                image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/20e3772a6dbe2804ecd1dc67064325ebbb316b23/PeaceOfMind.png'
              }
            ].map((feature, i) => (
              <div key={i} style={{
                background: '#ffffff',
                borderRadius: 'clamp(1rem, 2vw, 1.125rem)',
                padding: 'clamp(2rem, 4vw, 3rem)',
                boxShadow: '0 14px 34px 0 rgba(0,0,0,0.06)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(18,60,59,0.10)',
              textAlign: 'center',
                  display: 'flex', 
              flexDirection: 'column',
                alignItems: 'center'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 18px 44px 0 rgba(18,60,59,0.14)';
                e.currentTarget.style.transform = 'translateY(-8px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 14px 34px 0 rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <img 
                  src={feature.image}
                  alt={feature.title}
                  style={{
                    width: 'clamp(80px, 15vw, 120px)',
                    height: 'auto',
                    marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
                    filter: 'drop-shadow(0 4px 8px rgba(2, 132, 199, 0.3))'
                  }}
                />
                <h3 style={{ 
                  fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.25rem)',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  margin: 0,
                  color: '#0F172A',
                  marginBottom: 'clamp(1rem, 2vw, 1.5rem)'
                }}>
                  {feature.title}
              </h3>
              <p style={{ 
                  fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
                  lineHeight: 1.6,
                  color: '#475569',
                  margin: 0
                }}>
                  {feature.description}
              </p>
            </div>
            ))}
          </div>
        </ContentLimiter>
      </section>

      {/* How It Works */}
      <section style={{
        background: 'linear-gradient(135deg, #123c3b, #0d2928)',
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <ContentLimiter>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
            gap: 'clamp(2rem, 6vw, 4rem)',
            alignItems: 'center'
          }}>
            {/* Left - Content */}
            <div>
              <h2 style={{ 
                fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                margin: 0,
                  color: '#ffffff',
                marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
              }}>
                Compare Contractor Prices
              </h2>
              
              <div style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
                {[
                  { step: '1', title: 'Enter Address', desc: 'Where the work will be done.' },
                  { step: '2', title: 'Describe Your Project', desc: 'Quick, simple form.' },
                  { step: '3', title: 'Request Bids', desc: 'Project details are sent to local pros who compete for your project.' },
                  { step: '4', title: 'Get Aerial Measurements', desc: 'no home visits.' },
                  { step: '5', title: 'Get Multiple Bids', desc: 'Delivered to your inbox: fast, free, no obligation.' }
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    gap: 'clamp(0.75rem, 2vw, 1rem)',
                    marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{
                      color: '#FBBF24',
                      fontWeight: 800,
                      fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
                      minWidth: '30px',
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                    }}>
                      {item.step}.
                    </span>
                    <span style={{ 
                      fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
                      lineHeight: 1.6,
                      color: '#ffffff'
                    }}>
                      <strong style={{ 
                        color: '#FBBF24',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                      }}>
                        {item.title}
                      </strong> — {item.desc}
                    </span>
        </div>
                ))}
                    </div>

                <p style={{
                fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
                lineHeight: 1.6,
                  color: '#ffffff',
                fontWeight: 600 
                }}>
                  Skip the calls & House Visits. Save time, compare prices, and choose with confidence.
                </p>
        </div>
        
            {/* Right - iPhone Mockup */}
            <div style={{
              position: 'relative',
              width: 'min(360px, 92vw)',
              aspectRatio: '9/17.55',
              margin: '0 auto',
              borderRadius: '42px',
              boxShadow: '0 0 0 2px rgba(255,255,255,.06) inset, 0 20px 60px rgba(0,0,0,.6), 0 5px 25px rgba(0,0,0,.45)',
              background: 'linear-gradient(135deg, #8E8E93 0%, #636366 100%)',
              padding: '14px'
            }}>
              {/* Glow Effect */}
              <div style={{
                position: 'absolute',
                inset: '-25% -25% auto auto',
                width: '70%',
                aspectRatio: '1/1',
                  borderRadius: '50%', 
                background: 'radial-gradient(circle at 50% 50%, rgba(124,58,237,.35), transparent 65%)',
                filter: 'blur(40px)',
                pointerEvents: 'none',
                mixBlendMode: 'screen',
                transform: 'translate(10%, -10%)',
                zIndex: 1
              }}></div>
              
              {/* Notch */}
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '40%',
                height: '26px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #8E8E93 0%, #636366 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,.5) inset',
                pointerEvents: 'none',
                zIndex: 3
              }}></div>
              
              {/* Screen */}
              <div style={{
                position: 'absolute',
                inset: '14px',
                borderRadius: 'calc(42px - 14px)',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                boxShadow: '0 0 0 1px rgba(255,255,255,.04) inset',
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                zIndex: 2
              }}>
                {/* Header */}
                <div style={{
                  padding: '14px 14px 9px',
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  background: 'linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,0))',
                  borderBottom: '1px solid rgba(255,255,255,.06)'
                }}>
                  <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', fontWeight: '700', color: 'white' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#6ee7b7', boxShadow: '0 0 16px #6ee7b7' }}></span>
                    CoreBids
                  </div>
                </div>
                
                {/* Content */}
                <div style={{ padding: '10px', overflow: 'auto' }}>
                  <div style={{
                    background: 'rgba(255,255,255,.04)',
                    border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: '14px',
                    padding: '9px',
                    marginBottom: '9px',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <h3 style={{ margin: '1px 0 5px', fontSize: '15px', color: 'white' }}>1. Enter Address</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input 
                        type="text"
                        placeholder="Home Address"
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                        data-lpignore="true"
                        data-form-type="other"
                        className="no-autocomplete-styling"
                        style={{
                          flex: '1',
                          padding: '8px 14px',
                          borderRadius: '7px',
                          border: '1px solid rgba(255,255,255,.08)',
                          background: 'rgba(255,255,255,.06)',
                          color: 'white',
                          fontSize: '13px',
                          outline: 'none',
                          transition: 'border-color .2s ease',
                          WebkitAppearance: 'none',
                          appearance: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'rgba(255,255,255,.2)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'rgba(255,255,255,.08)';
                        }}
                      />
                      <button style={{
                        display: 'inline-flex',
                  alignItems: 'center', 
                  justifyContent: 'center', 
                        padding: '4px 14px',
                        borderRadius: '7px',
                        border: '1px solid rgba(255,255,255,.08)',
                        background: 'linear-gradient(180deg, rgba(124,58,237,.25), rgba(34,211,238,.25))',
                        color: 'white',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'transform .12s ease, box-shadow .12s ease, background .2s ease',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 4px 12px rgba(124,58,237,.25)'
                      }}>
                        Submit
                      </button>
            </div>
        </div>
                  
                  <div style={{
                    background: 'rgba(255,255,255,.04)',
                    border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: '14px',
                    padding: '7px 9px',
                    marginBottom: '9px',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <h3 style={{ margin: '0 0 3px', fontSize: '15px', color: 'white' }}>2. Describe Project</h3>
                    
                    {/* Step 0: Material Type */}
                    {currentStep === 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <h4 style={{ margin: '0 0 7px 0', fontSize: '13px', color: '#a1a1aa', fontWeight: '500' }}>What Type of Material?</h4>
                        <div className="material-buttons" style={{ display: 'flex', gap: '14px' }}>
                          {/* Left Column */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', flex: '1' }}>
                            <button 
                              onClick={(e) => {
                                const container = e.currentTarget.closest('.material-buttons');
                                if (container) {
                                  const allButtons = container.querySelectorAll('button');
                                  allButtons.forEach(btn => {
                                    btn.style.background = 'rgba(255,255,255,.06)';
                                    btn.style.borderColor = 'rgba(255,255,255,.08)';
                                    btn.style.color = 'white';
                                  });
                                }
                                const button = e.currentTarget;
                                button.style.background = 'rgba(59,130,246,.3)';
                                button.style.borderColor = 'rgba(59,130,246,.6)';
                                button.style.color = '#dbeafe';
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,.08)',
                                background: 'rgba(255,255,255,.06)',
                                color: 'white',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all .2s ease',
                                fontSize: '13px',
                                fontWeight: '500',
                                width: '100%',
                                minHeight: '32px'
                              }}>
                              Asphalt
                            </button>
                            <button 
                              onClick={(e) => {
                                const container = e.currentTarget.closest('.material-buttons');
                                if (container) {
                                  const allButtons = container.querySelectorAll('button');
                                  allButtons.forEach(btn => {
                                    btn.style.background = 'rgba(255,255,255,.06)';
                                    btn.style.borderColor = 'rgba(255,255,255,.08)';
                                    btn.style.color = 'white';
                                  });
                                }
                                const button = e.currentTarget;
                                button.style.background = 'rgba(59,130,246,.3)';
                                button.style.borderColor = 'rgba(59,130,246,.6)';
                                button.style.color = '#dbeafe';
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,.08)',
                                background: 'rgba(255,255,255,.06)',
                                color: 'white',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all .2s ease',
                                fontSize: '13px',
                                fontWeight: '500',
                                width: '100%',
                                minHeight: '32px'
                              }}>
                              Metal
                            </button>
                          </div>
                          {/* Right Column */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: '1' }}>
                            <button 
                              onClick={(e) => {
                                const container = e.currentTarget.closest('.material-buttons');
                                if (container) {
                                  const allButtons = container.querySelectorAll('button');
                                  allButtons.forEach(btn => {
                                    btn.style.background = 'rgba(255,255,255,.06)';
                                    btn.style.borderColor = 'rgba(255,255,255,.08)';
                                    btn.style.color = 'white';
                                  });
                                }
                                const button = e.currentTarget;
                                button.style.background = 'rgba(59,130,246,.3)';
                                button.style.borderColor = 'rgba(59,130,246,.6)';
                                button.style.color = '#dbeafe';
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,.08)',
                                background: 'rgba(255,255,255,.06)',
                                color: 'white',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all .2s ease',
                                fontSize: '13px',
                                fontWeight: '500',
                                width: '100%',
                                minHeight: '32px'
                              }}>
                              Tile
                            </button>
                            <button 
                              onClick={(e) => {
                                const container = e.currentTarget.closest('.material-buttons');
                                if (container) {
                                  const allButtons = container.querySelectorAll('button');
                                  allButtons.forEach(btn => {
                                    btn.style.background = 'rgba(255,255,255,.06)';
                                    btn.style.borderColor = 'rgba(255,255,255,.08)';
                                    btn.style.color = 'white';
                                  });
                                }
                                const button = e.currentTarget;
                                button.style.background = 'rgba(59,130,246,.3)';
                                button.style.borderColor = 'rgba(59,130,246,.6)';
                                button.style.color = '#dbeafe';
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,.08)',
                                background: 'rgba(255,255,255,.06)',
                                color: 'white',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all .2s ease',
                                fontSize: '13px',
                                fontWeight: '500',
                                width: '100%',
                                minHeight: '32px'
                              }}>
                              Flat
                            </button>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                          <button 
                            onClick={() => setCurrentStep(1)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '3px 48px',
                              borderRadius: '6px',
                              border: '1px solid rgba(255,255,255,.08)',
                              background: 'rgba(255,255,255,.06)',
                              color: 'white',
                              cursor: 'pointer',
                              userSelect: 'none',
                              transition: 'transform .12s ease, box-shadow .12s ease, background .2s ease',
                              fontSize: '13px',
                              fontWeight: '500',
                              minWidth: '80px'
                            }}>
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                      </div>
                  
                  <div style={{
                    background: 'rgba(255,255,255,.04)',
                    border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: '16px',
                    padding: '10px',
                    marginBottom: '10px',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <h3 style={{ margin: '0', fontSize: '16px', color: 'white' }}>3. Request Bids</h3>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255,255,255,.04)',
                    border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: '16px',
                    padding: '7px 10px',
                    marginBottom: '10px',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <h3 style={{ margin: '0 0 3px', fontSize: '16px', color: 'white' }}>4. Get Remote Measurements</h3>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                      <div style={{ 
                        width: '20px', 
                        height: '20px', 
                        borderRadius: '50%', 
                        border: '2px solid rgba(255,255,255,.5)',
                        borderTop: '2px solid #00ff00',
                        transform: 'rotate(0deg)',
                        transition: 'transform 0.1s linear'
                      }} 
                      ref={(el) => {
                        if (el) {
                          let rotation = 0;
                          const spin = () => {
                            rotation += 5;
                            el.style.transform = `rotate(${rotation}deg)`;
                            requestAnimationFrame(spin);
                          };
                          spin();
                        }
                      }}
                      />
                      <p style={{ margin: '0', color: '#a1a1aa', fontSize: '14px' }}>
                        Generating Aerial Imagery
                      </p>
                    </div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255,255,255,.04)',
                    border: '1px solid rgba(255,255,255,.08)',
                    borderRadius: '16px',
                    padding: '7px',
                    marginBottom: '14px',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                      <h3 style={{ margin: '0', fontSize: '16px', color: 'white' }}>5.</h3>
                      <button style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '1.5px 48px',
                        borderRadius: '10px',
                        border: '1px solid rgba(255,255,255,.08)',
                        background: 'linear-gradient(180deg, rgba(124,58,237,.25), rgba(34,211,238,.25))',
                        color: 'white',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'transform .12s ease, box-shadow .12s ease, background .2s ease',
                        boxShadow: '0 8px 20px rgba(124,58,237,.25)',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        View Multiple Bids
                      </button>
                    </div>
                  </div>
                </div>
                
                               {/* Footer */}
                 <div style={{ padding: '9px 14px 14px', display: 'flex', gap: '9px', justifyContent: 'center' }}>
                   <button 
                     onClick={() => {
                       const heroSection = document.querySelector('section[style*="background: linear-gradient"]');
                       if (heroSection) {
                         heroSection.scrollIntoView({ behavior: 'smooth' });
                       }
                     }}
                     style={{
                       display: 'inline-flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: '7px',
                       padding: '9px 13px',
                       borderRadius: '11px',
                       border: '1px solid rgba(255,255,255,.08)',
                       background: 'linear-gradient(180deg, rgba(124,58,237,.25), rgba(34,211,238,.25))',
                       color: 'white',
                       cursor: 'pointer',
                       userSelect: 'none',
                       transition: 'transform .12s ease, box-shadow .12s ease, background .2s ease',
                       boxShadow: '0 8px 20px rgba(124,58,237,.25)',
                       fontSize: '13px',
                       fontWeight: '600'
                     }}>
                     Get Started
                   </button>
                   <a 
                     href="/faq" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     style={{
                       display: 'inline-flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: '7px',
                       padding: '9px 13px',
                       borderRadius: '11px',
                       border: '1px solid rgba(255,255,255,.08)',
                       background: 'rgba(255,255,255,.06)',
                       color: 'white',
                       cursor: 'pointer',
                       userSelect: 'none',
                       transition: 'transform .12s ease, box-shadow .12s ease, background .2s ease',
                       fontSize: '13px',
                       fontWeight: '600',
                       textDecoration: 'none'
                     }}>
                     Learn More
                   </a>
                 </div>
              </div>
            </div>
          </div>
        </ContentLimiter>
      </section>

      {/* Testimonials */}
      <section style={{
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        background: '#ffffff'
      }}>
        <ContentLimiter>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: '#0F172A',
            textAlign: 'center',
            margin: 0,
            marginBottom: 'clamp(2rem, 4vw, 3rem)'
          }}>
            What Our Customers Say
          </h2>
        
        <div style={{ 
            position: 'relative',
          overflow: 'hidden',
            marginTop: 'clamp(2rem, 4vw, 3rem)'
        }}>
            <div style={{
              display: 'flex',
              transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: `translateX(-${(currentTestimonialIndex % testimonials.length) * 100}%)`,
              gap: '2rem'
            }}>
              {testimonials.map((testimonial, i) => (
                <div key={i} style={{
                  minWidth: '100%',
                  flexShrink: 0,
                  background: '#ffffff',
                  padding: 'clamp(2rem, 4vw, 3rem)',
                  borderRadius: 'clamp(0.75rem, 1.5vw, 1rem)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(18, 60, 59, 0.1)'
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    {Array.from({ length: testimonial.rating }).map((_, j) => (
                      <span key={j} style={{ 
                        color: '#FBBF24',
                        fontSize: 'clamp(1.25rem, 2vw, 1.5rem)'
                      }}>
                      ★
                    </span>
                ))}
              </div>
                <p style={{ 
                    fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
                    lineHeight: 1.6,
                  fontStyle: 'italic', 
                    marginBottom: '1rem',
                    color: '#475569'
                  }}>
                    "{testimonial.quote}"
                  </p>
                  <p style={{
                    fontWeight: 600,
                    color: '#123c3b',
                    margin: 0,
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)'
                  }}>
                    {testimonial.author}
                  </p>
            </div>
          ))}
        </div>
        </div>

          {/* Testimonial indicators */}
          <div style={{
            display: 'flex',
                                justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem'
          }}>
            {testimonials.map((_, i) => (
                            <button
                key={i}
                onClick={() => setCurrentTestimonialIndex(i)}
                              style={{
                  width: '12px',
                  height: '12px',
                        borderRadius: '50%', 
                  border: 'none',
                  background: (i % testimonials.length) === (currentTestimonialIndex % testimonials.length)
                    ? '#123c3b' 
                    : '#e2e8f0',
                        cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              />
                ))}
              </div>
        </ContentLimiter>
      </section>

      {/* Ready to Get Started? - Copy */}
      <section
        className="section"
        style={{
        background: 'linear-gradient(135deg, #123c3b, #0d2928)', 
        color: 'white', 
        borderRadius: '12px',
        marginBottom: '60px',
        textAlign: 'center',
        padding: '60px 40px'
        }}
      >
        <h2 style={{ fontSize: '36px', fontWeight: 700, marginBottom: '16px' }}>Ready to Get Started?</h2>
        <p
          style={{
            fontSize: '18px',
            color: '#ffffff',
            marginBottom: '32px',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          Get your free estimate today and save money on your roofing project
        </p>
        <button
          onClick={scrollToHero}
          style={{
            background: 'white',
            color: '#123c3b',
            border: 'none',
            padding: 'clamp(16px, 3vw, 24px) clamp(32px, 5vw, 48px)',
            borderRadius: 'clamp(12px, 2vw, 16px)',
            fontSize: 'clamp(18px, 3vw, 22px)',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 8px 25px rgba(255, 255, 255, 0.3)',
            letterSpacing: '0.5px'
          }}
          onMouseEnter={(e) => {
            const target = e.currentTarget as HTMLElement;
            target.style.transform = 'translateY(-2px)';
            target.style.boxShadow = '0 12px 35px rgba(255, 255, 255, 0.4)';
          }}
          onMouseLeave={(e) => {
            const target = e.currentTarget as HTMLElement;
            target.style.transform = 'translateY(0)';
            target.style.boxShadow = '0 8px 25px rgba(255, 255, 255, 0.3)';
          }}
        >
          Get My Free Estimates!
        </button>
      </section>

      
      {/* What's the Best Roofing Material */}
      <section style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
        padding: '40px 40px 120px 40px',
        marginBottom: '0px',
        marginTop: '-60px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
          <h2 style={{ 
            fontSize: 'clamp(42px, 6vw, 56px)', 
            fontWeight: '800', 
            color: '#1e293b', 
            marginBottom: '24px', 
            textAlign: 'center',
            letterSpacing: '-0.02em'
          }}>
            Need Help Describing Your Project?
          </h2>
          
          <p style={{
            fontSize: 'clamp(18px, 2.5vw, 20px)',
            lineHeight: '1.6',
            color: '#475569',
            marginBottom: '40px',
            textAlign: 'left',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            Years of experience have taught us which roofing materials perform best in harsh, snowy winters and brutally hot, humid summers. Extreme temperature swings can cause roofing to expand, contract, and wear down faster than expected—making material choice critical for durability and performance.
          </p>
          
          <p style={{
            fontSize: 'clamp(16px, 2vw, 18px)',
            lineHeight: '1.6',
            color: '#475569',
            marginBottom: '40px',
            textAlign: 'left',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            When choosing roofing materials, you have many options. CoreBids contractors will order and install any roofing type you prefer. To help you decide, here are our key considerations for each option:
          </p>
          
          <div style={{
            maxWidth: '800px',
            margin: '40px auto 0 auto',
            textAlign: 'left'
          }}>
                         <h3 style={{
               fontSize: 'clamp(20px, 2.5vw, 24px)',
               fontWeight: '700',
               color: '#1e293b',
               marginBottom: '16px',
               marginTop: '32px'
             }}>
               Asphalt Shingles
              </h3>
            <p style={{
              fontSize: 'clamp(16px, 2vw, 18px)',
              lineHeight: '1.6',
              color: '#475569',
              marginBottom: '24px'
            }}>
              Asphalt shingles are the most widely used roofing material in the U.S. They're cost-effective, versatile, and come in a wide range of colors and styles. Installation is quick, and repairs are simple if damage occurs. Many modern asphalt shingles are impact- and wind-resistant, giving them good performance in storms. Their drawback: they typically last 20–30 years, which is shorter than other premium options.
            </p>
            
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '32px'
            }}>
              <img 
                src="/media/asphalt-roof.png" 
                alt="Asphalt Shingles Roof" 
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: 'auto',
                  borderRadius: '8px',
                  border: '3px solid white',
                  boxShadow: '0 0 0 3px white'
                }}
              />
            </div>
            
                         <h3 style={{
               fontSize: 'clamp(20px, 2.5vw, 24px)',
               fontWeight: '700',
               color: '#1e293b',
               marginBottom: '16px',
               marginTop: '32px'
             }}>
               Metal Roof
             </h3>
            <p style={{
              fontSize: 'clamp(16px, 2vw, 18px)',
              lineHeight: '1.6',
              color: '#475569',
              marginBottom: '24px'
            }}>
              Metal roofing is our contractors' top recommendation for longevity and protection. It reflects heat, making it energy efficient in Nashville's humid summers, and sheds snow easily in the winter. Metal roofs are fire-, insect-, and rot-resistant, lasting 50+ years with minimal maintenance. While more expensive upfront, they often pay for themselves in durability and energy savings. The main drawback is potential noise during heavy rain or hail, though insulation can reduce this.
            </p>
            
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '32px'
            }}>
              <img 
                src="/media/metal-roof.png" 
                alt="Metal Roof" 
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: 'auto',
                  borderRadius: '8px',
                  border: '3px solid white',
                  boxShadow: '0 0 0 3px white'
                }}
              />
            </div>
            
                         <h3 style={{
               fontSize: 'clamp(20px, 2.5vw, 24px)',
               fontWeight: '700',
               color: '#1e293b',
               marginBottom: '16px',
               marginTop: '32px'
             }}>
               Tile Roof
             </h3>
            <p style={{
              fontSize: 'clamp(16px, 2vw, 18px)',
              lineHeight: '1.6',
              color: '#475569',
              marginBottom: '24px'
            }}>
              Tile roofs—whether clay or concrete—deliver unmatched aesthetics with a timeless, high-end look. They're exceptionally durable, lasting 50–100 years, and offer excellent fire and weather resistance. Tile's natural thermal properties also help regulate indoor temperatures. However, they're significantly heavier than other roofing materials, requiring reinforced support structures, and their installation cost is among the highest.
            </p>
            
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '32px'
            }}>
              <img 
                src="/media/tile-roof.png" 
                alt="Tile Roof" 
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: 'auto',
                  borderRadius: '8px',
                  border: '3px solid white',
                  boxShadow: '0 0 0 3px white'
                }}
              />
            </div>
            
                         <h3 style={{
               fontSize: 'clamp(20px, 2.5vw, 24px)',
               fontWeight: '700',
               color: '#1e293b',
               marginBottom: '16px',
               marginTop: '32px'
             }}>
               Flat Roof
             </h3>
            <p style={{
              fontSize: 'clamp(16px, 2vw, 18px)',
              lineHeight: '1.6',
              color: '#475569',
              marginBottom: '24px'
            }}>
              Flat roofs are common on modern homes and commercial buildings. They're made from materials like rubber or special membranes that help keep water out. Flat roofs are easier to walk on, which makes them great for adding solar panels or even a small rooftop deck. The downside is they don't last as long as sloped roofs (usually 15–25 years) and need regular checks to make sure water drains properly.
            </p>
            
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '32px'
            }}>
              <img 
                src="/media/flat-roof.png" 
                alt="Flat Roof" 
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: 'auto',
                  borderRadius: '8px',
                  border: '3px solid white',
                  boxShadow: '0 0 0 3px white'
                }}
              />
            </div>
            </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        background: '#f8fafc'
      }}>
        <ContentLimiter>
            <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: '#0F172A',
            textAlign: 'center',
            margin: 0,
            marginBottom: 'clamp(2rem, 4vw, 3rem)'
          }}>
            How Much Does a Roof Replacement Cost?
            </h2>
            
          <div style={{
            overflowX: 'auto',
            marginTop: 'clamp(2rem, 4vw, 3rem)'
          }}>
            <table style={{
              width: '100%',
              minWidth: '600px',
              borderCollapse: 'collapse',
              background: '#ffffff',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <thead>
                <tr>
                  <th style={{ 
                    padding: 'clamp(1rem, 2vw, 1.5rem)',
                    textAlign: 'left',
                    borderBottom: '2px solid #e2e8f0',
                    fontWeight: 700,
                    color: '#0F172A',
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)'
                  }}>
                    Roofing Material
                  </th>
                  <th style={{ 
                    padding: 'clamp(1rem, 2vw, 1.5rem)',
                    textAlign: 'center', 
                    borderBottom: '2px solid #e2e8f0',
                    fontWeight: 700,
                    color: '#0F172A',
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)'
                  }}>
                    Cost per Sq Ft
                  </th>
                  <th style={{ 
                    padding: 'clamp(1rem, 2vw, 1.5rem)',
                    textAlign: 'center', 
                    borderBottom: '2px solid #e2e8f0',
                    fontWeight: 700,
                    color: '#0F172A',
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)'
                  }}>
                    Total (2,000 sq ft)
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: '3-Tab Asphalt Shingles', costPerSqFt: '$4.00', totalCost: '$8,000' },
                  { name: 'Architectural Asphalt Shingles', costPerSqFt: '$5.86', totalCost: '$11,720' },
                  { name: 'Luxury Asphalt Shingles', costPerSqFt: '$8.50', totalCost: '$17,000' },
                  { name: 'Steel Exposed Fastener Metal', costPerSqFt: '$4.50', totalCost: '$9,000' },
                  { name: 'Steel Standing Seam Metal', costPerSqFt: '$16.00', totalCost: '$32,000' },
                  { name: 'Copper Standing Seam Metal', costPerSqFt: '$30.00 - $50.00', totalCost: '$60,000 - $100,000' },
                  { name: 'Synthetic Shingles', costPerSqFt: '$15.00 - $20.00', totalCost: '$30,000 - $40,000' },
                  { name: 'Cedar Shake', costPerSqFt: '$25.00', totalCost: '$50,000' },
                  { name: 'Slate Tiles', costPerSqFt: '$25.00 - $30.00', totalCost: '$50,000 - $60,000' },
                  { name: 'EPDM (Flat Roof)', costPerSqFt: '$14.00', totalCost: '$28,000' },
                  { name: 'PVC (Flat Roof)', costPerSqFt: '$14.00', totalCost: '$28,000' },
                  { name: 'TPO (Flat Roof)', costPerSqFt: '$14.00', totalCost: '$28,000' },
                ].map((material, i) => (
                  <tr key={i} style={{
                    background: i % 2 === 0 ? '#ffffff' : '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <td style={{
                      padding: 'clamp(0.875rem, 1.5vw, 1.25rem)',
                      fontWeight: 600,
                      color: '#0F172A',
                      fontSize: 'clamp(0.875rem, 1.5vw, 1rem)'
                    }}>
                      {material.name}
                  </td>
                    <td style={{
                      padding: 'clamp(0.875rem, 1.5vw, 1.25rem)',
                      textAlign: 'center',
                      color: '#123c3b',
                      fontWeight: 600,
                      fontSize: 'clamp(0.875rem, 1.5vw, 1rem)'
                    }}>
                      {material.costPerSqFt}
                  </td>
                    <td style={{
                      padding: 'clamp(0.875rem, 1.5vw, 1.25rem)',
                      textAlign: 'center',
                      color: '#123c3b',
                      fontWeight: 600,
                      fontSize: 'clamp(0.875rem, 1.5vw, 1rem)'
                    }}>
                      {material.totalCost}
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p style={{
            textAlign: 'center',
            marginTop: 'clamp(1rem, 2vw, 1.5rem)',
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            color: '#64748b'
          }}>
            Prices are estimates and may vary by location and contractor.
            </p>
        </ContentLimiter>
      </section>

      {/* Financing Section */}
      <section style={{
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        background: '#ffffff'
      }}>
        <ContentLimiter>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: '#0F172A',
            textAlign: 'center',
            margin: 0,
            marginBottom: 'clamp(1rem, 2vw, 1.5rem)'
          }}>
            Flexible Financing Options Available
          </h2>
          
          <p style={{
            fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
            lineHeight: 1.6,
            textAlign: 'center',
            color: '#475569',
            maxWidth: '800px',
            margin: '0 auto clamp(2rem, 4vw, 3rem)'
          }}>
            CoreBids partners with Enhancify to offer flexible financing solutions for your roofing project. 
            Get approved quickly and choose a payment plan that fits your budget. Calculate your monthly payments below 
            and explore financing options to make your roof replacement more affordable.
          </p>

          {/* Payment Calculator Widget */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 'clamp(2rem, 4vw, 3rem)'
          }}>
            <div 
              id='paymentcalculatorwidget' 
              data-defaultscheme='false' 
              data-color1='#68BA62' 
              data-color2='#1C418C' 
              data-cobrandedcolor='#FFFFFF' 
              data-border='false' 
              data-page='9931908' 
              data-hidelink='0'
              style={{
                width: '100%',
                maxWidth: '800px'
              }}
            />
          </div>

          {/* Enhancify Banner */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 'clamp(2rem, 4vw, 3rem)'
          }}>
            <a
              href="https://www.enhancify.com/corebids?utm_source=House_Green_930x180&from=https%3A%2F%2Fwww.corebids.com%2F"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                position: 'relative',
                display: 'block',
                width: '100%',
                maxWidth: '930px',
                height: '180px',
                textDecoration: 'none',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <iframe
                src='https://www.enhancify.com/banner?name=House_Green_930x180&page=9931908&hideLink=0'
                title="Enhancify Financing Banner"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  pointerEvents: 'none'
                }}
                referrerPolicy='unsafe-url'
              />
              <span style={{
                position: 'absolute',
                bottom: '12px',
                right: '16px',
                color: '#ffffff',
                fontSize: '40px',
                fontWeight: 'bold',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                pointerEvents: 'auto',
                zIndex: 10,
                cursor: 'pointer'
              }}>
                Click Here!
              </span>
            </a>
        </div>
        </ContentLimiter>
      </section>

      {/* FAQ Section */}
      <section style={{
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        background: '#f8fafc'
      }}>
        <ContentLimiter>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: '#0F172A',
            textAlign: 'center',
            margin: 0,
            marginBottom: 'clamp(2rem, 4vw, 3rem)'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {[
              {
                q: 'Do you need to visit my home?',
                a: 'No. We use remote measurements first. Contractors visit only after you pick a bid.'
              },
              {
                q: 'How fast do I get bids?',
                a: 'Most homeowners see bids within 24–48 hours after entering their address.'
              },
              {
                q: 'Do I have to accept a bid?',
                a: 'No obligation. You can review, ask questions, or walk away anytime.'
              },
              {
                q: 'Do you offer financing?',
                a: 'Yes. We offer flexible financing options so you can spread payments over time if you qualify.'
              },
              {
                q: 'How does payment work?',
                a: 'Your payment is held securely in escrow and only released after you approve completed work.'
              }
            ].map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              
              return (
                <div key={idx} style={{
                  background: '#ffffff',
                  border: `2px solid ${isOpen ? '#123c3b' : '#e2e8f0'}`,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}>
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'clamp(1rem, 2vw, 1.5rem)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{
                      fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.125rem)',
                      fontWeight: 600,
                      color: '#0F172A',
                      flex: 1
                    }}>
                      {faq.q}
                    </span>
                    <span style={{
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#123c3b',
                      marginLeft: '1rem',
                      flexShrink: 0
                    }}>
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>

                  {isOpen && (
                    <div style={{
                      padding: `0 clamp(1rem, 2vw, 1.5rem) clamp(1rem, 2vw, 1.5rem) clamp(1rem, 2vw, 1.5rem)`,
                      fontSize: 'clamp(0.875rem, 1.25vw + 0.125rem, 1rem)',
                      color: '#475569',
                      lineHeight: 1.6
                    }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ContentLimiter>
      </section>

      {/* Get My Estimates Section */}
      <section className="section" style={{ 
        marginBottom: '0px', 
        background: 'linear-gradient(135deg, #123c3b, #0d2928)', 
        padding: '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '60px' }}>
            <div style={{ flex: '1', textAlign: 'center' }}>
              <img
                src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/CoreBidsMan.png"
                alt="CoreBids Professional"
                style={{
                  width: '562px',
                  height: '469px',
                  borderRadius: '16px',
                  objectFit: 'contain'
                }}
              />
            </div>
            <div style={{ flex: '1' }}>
              <h2 style={{ 
                fontSize: 'clamp(42px, 6vw, 56px)', 
                fontWeight: '800', 
                color: 'white', 
                marginBottom: '24px',
                letterSpacing: '-0.02em'
              }}>
                Want to know your exact roofing cost?
              </h2>
              <p style={{ 
                fontSize: 'clamp(20px, 2.5vw, 24px)', 
                color: 'white', 
                lineHeight: '1.6', 
                marginBottom: '32px'
              }}>
                Request your FREE online roof estimates now!
              </p>
              <button
                onClick={scrollToHero}
                style={{
                  backgroundColor: 'white',
                  color: '#123c3b',
                  border: 'none',
                  padding: 'clamp(16px, 3vw, 24px) clamp(32px, 5vw, 48px)',
                  borderRadius: 'clamp(12px, 2vw, 16px)',
                  fontSize: 'clamp(18px, 3vw, 22px)',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget as HTMLElement;
                  target.style.backgroundColor = '#f3f4f6';
                  target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget as HTMLElement;
                  target.style.backgroundColor = 'white';
                  target.style.transform = 'scale(1)';
                }}
              >
                Get My Estimates Today!
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section style={{
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        background: '#ffffff'
      }}>
        <ContentLimiter>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: '#0F172A',
            textAlign: 'center',
            margin: 0,
            marginBottom: 'clamp(1rem, 2vw, 1.5rem)'
          }}>
              What are Your Next Steps?
            </h2>

          <p style={{
            fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
            lineHeight: 1.6,
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto clamp(2rem, 4vw, 3rem)',
            color: '#475569'
          }}>
            When choosing a roofing contractor, it's important to know the difference between quality work and shortcuts. 
            At CoreBids, we only approve verified, licensed contractors—so you can trust the team working on your home.
          </p>

          {[
            {
              title: 'Accept Your Roofing Bid',
              description: 'Picking the right contractor is easy and stress-free. You can review bids right from home:',
              points: [
                'See multiple quotes from verified contractors',
                'Compare prices and services side by side',
                'Choose the contractor that fits your needs best'
              ],
              image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step0.png'
            },
            {
              title: 'Old Roof Removal',
              description: 'Before installing a new roof, the old one must be removed safely. Your roofing crew will:',
              points: [
                'Strip away shingles, underlayment, and nails',
                'Protect your yard with tarps and careful cleanup',
                'Remove and dispose of all old materials'
              ],
              image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step1.png'
            },
            {
              title: 'Decking Inspection',
              description: 'The wood under your roof needs to be solid. Early checks prevent big problems later:',
              points: [
                'Look for soft or rotting wood that can\'t support shingles',
                'Check for water damage or mold',
                'Identify any structural issues that need fixing'
              ],
              image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step2.png'
            },
            {
              title: 'Weatherproof Underlayment Installation',
              description: 'A strong underlayment keeps your home safe from leaks and bad weather:',
              points: [
                'Roll out a waterproof layer over the roof decking',
                'Add extra protection in valleys and edges where leaks are common',
                'Install metal edges to guide water away from your home'
              ],
              image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step3.png'
            },
            {
              title: 'Professional Roofing Installation',
              description: 'With a solid base in place, your new roof goes on smoothly:',
              points: [
                'Install shingles, metal panels, or tiles correctly',
                'Align rows for long-lasting durability and a neat look',
                'Secure flashing around chimneys, vents, and skylights'
              ],
              image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step4.png'
            },
            {
              title: 'Final Inspection & Clean Up',
              description: 'A finished roof should look perfect and leave your home clean:',
              points: [
                'Remove all leftover materials and debris',
                'Inspect the work for quality and safety',
                'Walk through with you to make sure you\'re satisfied'
              ],
              image: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step5.png'
            }
          ].map((step, i) => (
            <div key={i}>
        <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(350px, 100%), 1fr))',
                gap: 'clamp(2rem, 4vw, 3rem)',
              alignItems: 'center',
                marginBottom: 'clamp(2rem, 4vw, 3rem)'
              }}>
                {/* Image */}
        <div style={{
                  order: i % 2 === 0 ? 2 : 1,
              display: 'flex',
                  justifyContent: 'center'
            }}>
              <img 
                    src={step.image}
                    alt={step.title}
                style={{
                  width: '100%',
                      maxWidth: i === 0 ? '350px' : '400px',
                      height: 'auto',
                  borderRadius: '16px',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                    }}
                  />
        </div>

                {/* Content */}
        <div style={{
                  order: i % 2 === 0 ? 1 : 2
                }}>
                  <h3 style={{ 
                    fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.25rem)',
                    fontWeight: 700,
                    lineHeight: 1.3,
                    color: '#0F172A',
                    margin: 0,
                    marginBottom: '1rem'
                  }}>
                    {step.title}
            </h3>
                  <p style={{ 
                    fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
                    lineHeight: 1.6,
                    color: '#475569',
                    marginBottom: '1rem'
                  }}>
                    {step.description}
                  </p>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {step.points.map((point, j) => (
                      <li key={j} style={{
              display: 'flex',
                        gap: '0.75rem',
                        marginBottom: '0.75rem',
                        alignItems: 'flex-start'
                      }}>
                        <span style={{
                          color: '#123c3b',
                          fontSize: '1.25rem',
                          lineHeight: 1,
                          marginTop: '0.125rem',
                          flexShrink: 0
                        }}>
                          •
                        </span>
                        <span style={{
                          fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
                          lineHeight: 1.6,
                          color: '#475569'
                        }}>
                          {point}
                        </span>
                      </li>
                    ))}
            </ul>
          </div>
        </div>

              {i < 5 && (
        <div style={{
          height: '1px',
                  background: '#e2e8f0',
                  margin: 'clamp(2rem, 4vw, 3rem) auto',
                  maxWidth: '600px'
                }} />
              )}
          </div>
          ))}
        </ContentLimiter>
      </section>

      {/* Final CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #123c3b, #0d2928)',
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        textAlign: 'center',
        color: '#ffffff'
      }}>
        <ContentLimiter>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: '#ffffff',
            margin: 0,
            marginBottom: 'clamp(1rem, 2vw, 1.5rem)'
          }}>
            Ready to Get Started?
          </h2>
          <p style={{ 
            fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
            lineHeight: 1.6,
            color: '#ffffff',
            maxWidth: '600px',
            margin: '0 auto clamp(1.5rem, 3vw, 2rem)'
          }}>
          Get your free estimate today and save money on your roofing project
        </p>
        <button
            onClick={scrollToHero}
          style={{
              background: '#ffffff',
              color: '#123c3b',
            border: 'none',
              padding: 'clamp(0.875rem, 2vw, 1.25rem) clamp(1.5rem, 4vw, 2.5rem)',
              borderRadius: '12px',
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            fontWeight: 700,
            cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
          }}
        >
          Get My Free Estimates!
        </button>
        </ContentLimiter>
      </section>

      {/* Service Area */}
      <section style={{
        padding: 'clamp(3rem, 8vw, 5rem) 0',
        background: '#f8fafc'
      }}>
        <ContentLimiter>
          <div style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: '#123c3b',
              margin: 0,
              marginBottom: 'clamp(1rem, 2vw, 1.5rem)'
            }}>
              Where Do We Service?
            </h2>
            
            <p style={{
              fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.25rem)',
              lineHeight: 1.6,
              color: '#475569',
              marginBottom: 0
            }}>
              Serving homeowners nationwide, CoreBids matches your project with vetted local contractors—no matter where you're located.
            </p>
          </div>
        </ContentLimiter>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#123c3b',
        padding: 'clamp(2rem, 5vw, 3rem) 0',
        color: '#cbd5e1'
      }}>
        <ContentLimiter>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: 'clamp(2rem, 4vw, 3rem)'
          }}>
            {/* Company */}
            <div>
              <h3 style={{ 
                color: '#ffffff',
                fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.125rem)',
                fontWeight: 600,
                margin: 0,
                marginBottom: '1rem'
              }}>
                Company
              </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate('about');
                    }}
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    About Us
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="mailto:info@corebids.com?subject=Homeowner%20Support"
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Contact Us
                  </a>
                </li>
            </ul>
          </div>

            {/* For Homeowners */}
            <div>
              <h3 style={{ 
                color: '#ffffff',
                fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.125rem)',
                fontWeight: 600,
                margin: 0,
                marginBottom: '1rem'
              }}>
                For Homeowners
              </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="/faq#how-it-works"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate('faq');
                      setTimeout(() => {
                        const el = document.getElementById('how-it-works');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    How It Works
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="#"
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Pricing
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate('faq');
                    }}
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    FAQ
                  </a>
                </li>
            </ul>
          </div>

            {/* For Contractors */}
            <div>
              <h3 style={{ 
                color: '#ffffff',
                fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.125rem)',
                fontWeight: 600,
                margin: 0,
                marginBottom: '1rem'
              }}>
                For Contractors
              </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate('contractor-landing');
                    }}
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Join Our Network
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="/contractor-landing#contractor-faq"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate('contractor-landing');
                      setTimeout(() => {
                        const el = document.getElementById('contractor-faq');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Contractor Benefits
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="mailto:info@corebids.com?subject=Contractor%20Support"
                    style={{
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Support
                  </a>
                </li>
            </ul>
        </div>
        
            {/* Legal */}
            <div>
              <h3 style={{ 
                color: '#ffffff',
                fontSize: 'clamp(1rem, 1.5vw + 0.125rem, 1.125rem)',
                fontWeight: 600,
                margin: 0,
                marginBottom: '1rem'
              }}>
                Legal
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
              style={{
                      color: '#cbd5e1',
                textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Terms of Service
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate('privacy-policy');
                    }}
              style={{
                      color: '#cbd5e1',
                textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Privacy Policy
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a 
                    href="/accessibility"
                    target="_blank"
                    rel="noopener noreferrer"
              style={{
                      color: '#cbd5e1',
                textDecoration: 'none',
                      fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    Accessibility
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Social */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'clamp(1.5rem, 4vw, 2.5rem)',
            paddingTop: 'clamp(1.5rem, 3vw, 2rem)',
            borderTop: '1px solid #334155',
            marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
          }}>
            {[
              { Icon: FaFacebook, color: '#1877F2', url: 'https://www.facebook.com/profile.php?id=61579328875227', label: 'Facebook' },
              { Icon: FaInstagram, color: '#E4405F', url: 'https://instagram.com/corebids', label: 'Instagram' },
              { Icon: FaTwitter, color: '#1DA1F2', url: 'https://twitter.com/corebids', label: 'Twitter' },
              { Icon: FaLinkedin, color: '#0077B5', url: 'https://linkedin.com/company/corebids', label: 'LinkedIn' }
            ].map((social, i) => (
              <a
                key={i}
                href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                  gap: '0.5rem',
                  textDecoration: 'none',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <social.Icon size={28} color={social.color} />
                <span style={{ 
                  color: '#cbd5e1',
                  fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                  fontWeight: 600
                }}>
                  {social.label}
              </span>
              </a>
            ))}
        </div>
        
          {/* Copyright */}
        <div style={{ 
          textAlign: 'center', 
            paddingTop: 'clamp(1rem, 2vw, 1.5rem)',
            borderTop: '1px solid #334155',
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            color: '#94a3b8'
          }}>
            <p style={{ margin: 0 }}>
              © 2024 CoreBids. All rights reserved. | Serving Nashville, Knoxville, and Chattanooga
            </p>
        </div>
        </ContentLimiter>
      </footer>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        .rotating-text-fade {
          transition: all 0.1s ease-in-out;
        }
        
        .float-out {
          transform: translateY(-20px);
          opacity: 0;
        }
        /* CB:BEGIN landing-mobile-images */
        @media (max-width: 600px) {
          .section img { max-width: 100% !important; height: auto !important; }
          .section div[style*='width: 450px'] { width: 100% !important; height: auto !important; }
        }
        /* CB:END landing-mobile-images */
      `}</style>
      
    </ResponsiveContainer>
  );
};

export default LandingPage; 
